import React, { useEffect, useState } from 'react';
import { DrillResponse, CreatePlayerWallPostRequest } from '../Model';
import { restClient } from '../RestClient';
import { useReferenceData } from '../ReferenceDataContext';
import { getDifficultyColor } from '../Drills/drillColors';
import { YouTubeEmbed, getYouTubeVideoId } from '../YouTubeEmbed';

interface ShareDrillModalProps {
  playerName: string;
  playerIds: number[];
  selectionDataId: number;
  skillFocus?: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string, type?: 'error' | 'warning' | 'info') => void;
}

export const ShareDrillModal: React.FC<ShareDrillModalProps> = ({
  playerName,
  playerIds,
  selectionDataId,
  skillFocus,
  onClose,
  onSuccess,
  onError,
}) => {
  const { currentSport } = useReferenceData();
  const [drills, setDrills] = useState<DrillResponse[]>([]);
  const [filteredDrills, setFilteredDrills] = useState<DrillResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDrillId, setSelectedDrillId] = useState<number | null>(null);
  const [expandedDrillId, setExpandedDrillId] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSkillFilter, setActiveSkillFilter] = useState<string | null>(skillFocus ?? null);

  // Load drills on mount
  useEffect(() => {
    let isMounted = true;
    const loadDrills = async () => {
      setIsLoading(true);
      const result = await restClient.getDrills(currentSport);
      if (!isMounted) return;
      if (result.IsSuccess) {
        setDrills(result.Value);
      } else {
        onError('Failed to load drills');
      }
      setIsLoading(false);
    };
    loadDrills();
    return () => { isMounted = false; };
  }, [currentSport, onError]);

  // Filter drills by skill focus and search query
  useEffect(() => {
    let base = drills;
    if (activeSkillFilter) {
      const filter = activeSkillFilter.toLowerCase();
      base = base.filter(d => d.drill.skills.some(s => s.toLowerCase() === filter));
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      base = base.filter(
        (d) =>
          d.drill.name.toLowerCase().includes(query) ||
          d.drill.description.toLowerCase().includes(query) ||
          d.drill.skills.some(s => s.toLowerCase().includes(query))
      );
    }
    setFilteredDrills(base);
  }, [searchQuery, drills, activeSkillFilter]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleSubmit = async () => {
    if (!selectedDrillId || !comment.trim()) return;

    setIsSubmitting(true);
    const request: CreatePlayerWallPostRequest = {
      selectionDataId,
      playerIds,
      drillId: selectedDrillId,
      comment: comment.trim(),
    };

    const result = await restClient.createPlayerWallPost(request);
    if (result.IsSuccess) {
      const count = result.Value.length;
      const target = playerIds.length === 1 ? playerName : `${count} player${count !== 1 ? 's' : ''}`;
      onSuccess(`Drill shared with ${target}`);
      onClose();
    } else {
      onError(result.Error?.description ?? 'Failed to share drill');
    }
    setIsSubmitting(false);
  };

  const selectedDrill = drills.find(d => d.drill.id === selectedDrillId);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-drill-modal-title"
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div className="flex-1">
            <h2 id="share-drill-modal-title" className="modal-title">Share Drill</h2>
            <p className="text-muted text-sm m-0">
              To: <strong>{playerName}</strong>
            </p>
          </div>
          <button onClick={onClose} className="btn-close btn-close-lg ml-4" aria-label="Close">
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search drills by name, description, or skill..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control w-full"
            />
            {activeSkillFilter && (
              <div className="flex items-center gap-2 mt-2">
                <span className="badge badge-accent flex items-center gap-1">
                  {activeSkillFilter}
                  <button
                    type="button"
                    onClick={() => setActiveSkillFilter(null)}
                    className="btn-close-inline"
                    aria-label="Clear skill filter"
                  >
                    ×
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* Status */}
          <div className="text-muted text-sm mb-3">
            {isLoading
              ? 'Loading drills...'
              : `${filteredDrills.length} drill${filteredDrills.length !== 1 ? 's' : ''} available`}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="text-center p-6">
              <div className="text-muted">Loading...</div>
            </div>
          )}

          {/* Drill List */}
          {!isLoading && filteredDrills.length > 0 && (
            <div className="drill-swap-list">
              {filteredDrills.map((drillResponse) => {
                const drill = drillResponse.drill;
                const isSelected = selectedDrillId === drill.id;
                const isExpanded = expandedDrillId === drill.id;
                const difficultyColors = getDifficultyColor(drill.difficulty);
                const videoId = drill.youtubeUrl ? getYouTubeVideoId(drill.youtubeUrl) : null;

                return (
                  <div
                    key={drill.id}
                    className={`drill-swap-item ${isSelected ? 'drill-swap-item-selected' : ''}`}
                    onClick={() => setSelectedDrillId(drill.id)}
                  >
                    {/* Header area */}
                    <div>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="drill-swap-name">{drill.name}</span>
                            <span
                              className="badge badge-sm"
                              style={{
                                backgroundColor: difficultyColors.bg,
                                color: difficultyColors.text,
                                border: `1px solid ${difficultyColors.border}`,
                              }}
                            >
                              {drill.difficulty}
                            </span>
                            <span className="badge badge-sm badge-secondary">
                              {drill.duration} min
                            </span>
                            <span className="badge badge-sm badge-secondary">
                              {drill.category}
                            </span>
                          </div>
                          {drill.skills.length > 0 && (
                            <div className="text-sm text-muted">
                              {drill.skills.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expand/Collapse button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDrillId(isExpanded ? null : drill.id);
                      }}
                      className="drill-swap-expand-btn"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? `Collapse ${drill.name}` : `Expand ${drill.name}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`drill-swap-expand-icon ${isExpanded ? 'drill-swap-expand-icon-rotated' : ''}`}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                      <span>{isExpanded ? 'Hide details' : 'View details'}</span>
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="drill-swap-expanded">
                        <p className="text-sm mb-3">{drill.description}</p>

                        {drill.skills.length > 0 && (
                          <div className="drill-swap-skills">
                            <div className="text-sm text-muted drill-swap-skills-label">Skills:</div>
                            <div className="flex flex-wrap gap-2">
                              {drill.skills.map((skill) => (
                                <span key={skill} className="badge badge-sm badge-secondary">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {videoId && drill.youtubeUrl && (
                          <div className="drill-swap-video">
                            <YouTubeEmbed
                              url={drill.youtubeUrl}
                              title={`${drill.name} - training video`}
                              borderRadius={6}
                            />
                          </div>
                        )}

                        {!drill.youtubeUrl && (
                          <div className="text-sm text-muted drill-swap-no-video">
                            No video available for this drill.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* No Results */}
          {!isLoading && filteredDrills.length === 0 && (
            <div className="text-center p-6">
              <p className="text-muted m-0">
                {searchQuery ? 'No drills match your search.' : 'No drills available.'}
              </p>
            </div>
          )}

          {/* Comment */}
          {selectedDrill && (
            <div className="mt-4">
              <label className="form-label">
                Message for {playerName}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment about this drill..."
                className="form-control w-full"
                rows={3}
                maxLength={2000}
              />
              <div className="text-sm text-muted mt-1">{comment.length}/2000</div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedDrillId || !comment.trim() || isSubmitting}
            className={`btn btn-primary ${(!selectedDrillId || !comment.trim() || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Sharing...' : 'Share Drill'}
          </button>
        </div>
      </div>
    </div>
  );
};
